// ExperienceClose — the one shared "So what?" close every interaction type uses.
//
// Implements the Tier 2+3 ending plan:
//   - Tier 2: authored framing with {token} slots filled from the learner's
//     actual captured values at play time (no live AI). A missing slot is
//     stripped gracefully (fall back to the authored body — never render
//     "{before}").
//   - Tier 3: outcome-specific authored variants; the first matching `when`
//     wins, else the top-level body.
//   - Optional reflection line (author decides whether to include it).
//
// This is also where the recurring "broken/flat ending" failure is fixed: a
// type renders <ExperienceClose> at its terminal state, so the experience always
// lands a close instead of dead-ending on a bare prompt.

import { useState } from 'react';
import { motion } from 'framer-motion';
import type {
  Theme,
  ExperienceConclusion,
  ExperienceBeat,
  ConclusionVariant,
  ConclusionCondition,
} from '../slideTypes';
import { springs } from '../../../ui/slide-animations';

/** Values a type exposes for {token} slots + variant matching, keyed by token name. */
export type ConclusionValues = Record<string, string | number | undefined>;

// Tokens may be flat (`{before}`) or block-scoped (`{first-call.correct}`) —
// composed experiences namespace by block id (kebab-case, so hyphens are valid)
// so two same-type blocks don't collide (§3.5.1).
const TOKEN = /\{([\w.-]+)\}/g;

/** Fill {token} slots from values; strip unfilled tokens and tidy the result. */
export function fillSlots(text: string, values: ConclusionValues): string {
  return text
    .replace(TOKEN, (_, key: string) => {
      const v = values[key];
      return v === undefined || v === null || v === '' ? '' : String(v);
    })
    .replace(/\s{2,}/g, ' ') // collapse spaces left by a removed token
    .replace(/\s+([.,;:!?])/g, '$1') // tidy space before punctuation
    .replace(/([([])\s+/g, '$1') // tidy space after an opening bracket
    .trim();
}

/** True if `text` has a {token} whose value is missing/empty in `values`. */
export function hasUnfilledToken(text: string, values: ConclusionValues): boolean {
  const matches = text.match(TOKEN);
  if (!matches) return false;
  return matches.some((m) => {
    const v = values[m.slice(1, -1)];
    return v === undefined || v === null || v === '';
  });
}

/**
 * Normalise authored close prose: replace em/en dashes used as connectors with a
 * comma (a classic AI tell, and house style bans them) and tidy. The value
 * arrow → is left intact. Runs on every rendered close, so a dash never reaches
 * the learner even if the Builder slips one in — a guarantee, not a request.
 */
export function normalizeProse(text: string): string {
  return text
    .replace(/\s*[—–]\s*/g, ', ') // em/en dash connector → comma
    .replace(/,\s*([.,;:!?])/g, '$1') // ", ." left by the swap → "."
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
}

function matchCondition(
  cond: ConclusionCondition,
  values: ConclusionValues,
  outcome?: string,
): boolean {
  if ('outcome' in cond) return outcome !== undefined && outcome === cond.outcome;
  const raw = values[cond.token];
  if (raw === undefined || raw === null || raw === '') return false;
  // eq/neq compare as strings (works for labels and numbers); ordering coerces
  // to number and bails if either side isn't numeric.
  if (cond.op === 'eq') return String(raw) === String(cond.value);
  if (cond.op === 'neq') return String(raw) !== String(cond.value);
  const a = Number(raw);
  const b = Number(cond.value);
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  switch (cond.op) {
    case 'lt':
      return a < b;
    case 'lte':
      return a <= b;
    case 'gt':
      return a > b;
    case 'gte':
      return a >= b;
  }
}

/**
 * Pick the body+heading to show: the first variant whose `when` matches (an
 * unconditional variant always matches), else the top-level body. Tokens are
 * NOT filled here — the caller fills via fillSlots so this stays pure/testable.
 */
export function selectConclusionBody(
  conclusion: ExperienceConclusion,
  values: ConclusionValues = {},
  outcome?: string,
): { heading?: string; body: string } {
  const variant: ConclusionVariant | undefined = conclusion.variants?.find(
    (v) => !v.when || matchCondition(v.when, values, outcome),
  );
  if (variant) {
    return { heading: variant.heading ?? conclusion.heading, body: variant.body };
  }
  return { heading: conclusion.heading, body: conclusion.body ?? '' };
}

interface ExperienceCloseProps {
  conclusion?: ExperienceConclusion;
  theme: Theme;
  /** The learner's captured values for {token} slots + variant matching. */
  values?: ConclusionValues;
  /** Discrete outcome id (e.g. scenario ending branch) for Tier-3 variants. */
  outcome?: string;
  /**
   * Token-free safety net. If the chosen body needs a value the learner didn't
   * give (skipped / junk input), render this clean sentence instead of a
   * half-stripped one like "You moved → ." The type supplies a sensible line.
   */
  fallback?: string;
  /**
   * The learner's own words, shown back to them (Tier-1 types: gallery-walk,
   * perspective-lenses, reflect-act). Each entry is a labelled response the
   * learner wrote. Empty values are skipped. Renders between body and reflection.
   */
  echoes?: { label: string; value: string }[];
  /** Capture the optional reflection line, when the author included one. */
  onReflection?: (text: string) => void;
}

/**
 * Renders the authored close. Returns null when there is no conclusion (a type
 * that hasn't adopted the close yet simply shows nothing — back-compat).
 */
export function ExperienceClose({
  conclusion,
  theme,
  values = {},
  outcome,
  fallback,
  echoes,
  onReflection,
}: ExperienceCloseProps) {
  const [reflection, setReflection] = useState('');
  const [saved, setSaved] = useState(false);
  // Staged-beat close (§3.5.2): how many beats are currently revealed. Starts at 1.
  const [revealed, setRevealed] = useState(1);

  if (!conclusion) return null;

  const selected = selectConclusionBody(conclusion, values, outcome);
  // Crap-input safety: if the chosen body needs a value the learner didn't give,
  // fall back to the clean token-free line rather than a half-stripped sentence.
  const rawBody =
    fallback && hasUnfilledToken(selected.body, values) ? fallback : selected.body;
  const heading = selected.heading
    ? normalizeProse(fillSlots(selected.heading, values))
    : undefined;
  const body = rawBody ? normalizeProse(fillSlots(rawBody, values)) : '';
  const accent = theme.colors.accent || theme.colors.primary;

  // Staged beats — each filled + normalised, empty ones dropped. When present
  // they REPLACE the single body block so the ending never lands as a wall of text.
  const beats: ExperienceBeat[] = (conclusion.beats ?? [])
    .map((b) => ({ heading: b.heading, body: normalizeProse(fillSlots(b.body, values)) }))
    .filter((b) => b.body.trim().length > 0);
  const hasBeats = beats.length > 0;
  const allBeatsRevealed = !hasBeats || revealed >= beats.length;

  const commit = () => {
    if (!reflection.trim()) return;
    onReflection?.(reflection.trim());
    setSaved(true);
  };

  return (
    <motion.div
      // Moment surface — rounded-2xl + elevated, per the design system.
      className="rounded-2xl border-l-[5px] p-6 shadow-lg"
      style={{ background: theme.colors.surface, borderColor: accent }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.gentle as object}
    >
      <p
        className="text-xs uppercase tracking-wider font-bold mb-2"
        style={{ color: accent }}
      >
        {heading || 'So what'}
      </p>

      {hasBeats ? (
        <div className="space-y-3">
          {beats.slice(0, revealed).map((beat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springs.gentle as object}
            >
              {beat.heading && (
                <p className="text-sm font-semibold mb-0.5" style={{ color: accent }}>
                  {beat.heading}
                </p>
              )}
              <p className="text-base leading-relaxed" style={{ color: theme.colors.text }}>
                {beat.body}
              </p>
            </motion.div>
          ))}
          {!allBeatsRevealed && (
            <button
              onClick={() => setRevealed((n) => Math.min(n + 1, beats.length))}
              className="text-sm font-semibold rounded-lg px-3 py-1.5"
              style={{ background: `${accent}18`, color: accent }}
            >
              Continue →
            </button>
          )}
        </div>
      ) : (
        <p className="text-base leading-relaxed" style={{ color: theme.colors.text }}>
          {body}
        </p>
      )}

      {allBeatsRevealed && echoes && echoes.filter((e) => e.value?.trim()).length > 0 && (
        <div className="mt-4 space-y-2">
          {echoes
            .filter((e) => e.value?.trim())
            .map((e, i) => (
              <div
                key={i}
                className="rounded-lg px-3 py-2"
                style={{ background: `${accent}10` }}
              >
                <span
                  className="block text-xs font-semibold mb-0.5"
                  style={{ color: accent }}
                >
                  {e.label}
                </span>
                <span className="text-sm" style={{ color: theme.colors.text }}>
                  {e.value}
                </span>
              </div>
            ))}
        </div>
      )}

      {allBeatsRevealed && conclusion.reflectionPrompt && (
        <div className="mt-4">
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: theme.colors.text }}
          >
            {conclusion.reflectionPrompt}
          </label>
          {saved ? (
            <p
              className="text-sm rounded-lg px-3 py-2"
              style={{ background: `${accent}12`, color: theme.colors.textMuted }}
            >
              {reflection}
            </p>
          ) : (
            <div className="flex gap-2">
              <input
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                }}
                className="flex-1 px-3 py-2 rounded-lg border text-sm"
                style={{
                  background: theme.colors.background,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                  fontFamily: theme.fonts.body,
                }}
              />
              <button
                onClick={commit}
                disabled={!reflection.trim()}
                className="px-4 py-2 rounded-lg font-semibold text-sm"
                style={{
                  background: accent,
                  color: '#fff',
                  opacity: reflection.trim() ? 1 : 0.4,
                  cursor: reflection.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
